import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import { connectionBadge, stateBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { ATTENTION_STATES, stateHint, urgency } from '../i18n/labels.js';
import { poll } from '../poll.js';
import { currentViewer } from '../viewer.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { overviewPageStyles } from '../../styles/pages/overview-page.js';

/** Landing page: what needs attention across servers and instances, and the latest applies. */
class OverviewPage extends LitElement {
  static styles = [
    pageStyles,
    tableStyles,
    overviewPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.poll = poll(this, async () => {
      const viewer = await currentViewer();
      // servers are the operator's business: a customer user gets none, and no 403 either
      const [instances, servers, events, requests] = await Promise.all([
        api.instances(),
        viewer.operator ? api.servers() : null,
        api.events({ limit: 60 }),
        viewer.operator ? api.enrollmentRequests() : [],
      ]);
      return { viewer, instances, servers, events, requests };
    });
  }

  renderTiles({ instances, servers }) {
    // servers is null for a customer user
    const count = (states) => instances.filter((instance) => states.includes(instance.state)).length;
    const attention = count(ATTENTION_STATES);
    const tiles = [
      { value: instances.length, label: 'Workspaces', href: '/workspaces' },
      { value: count(['running']), label: 'Running', tone: 'ok', href: '/workspaces?state=running' },
      { value: count(['updating']), label: 'Updating', tone: 'progress', href: '/workspaces?state=updating' },
      { value: attention, label: 'Need attention', tone: attention ? 'danger' : 'ok', href: '/workspaces?state=attention' },
    ];
    if (servers) {
      const online = servers.filter((server) => server.connection === 'online').length;
      tiles.push({ value: `${online}/${servers.length}`, label: 'Servers online', tone: online < servers.length ? 'warning' : 'ok', href: '/servers' });
    }
    return html`
      <div class="tiles">
        ${tiles.map(
          (tile) => html`
            <a class="tile" href=${tile.href} data-tone=${tile.tone ?? ''}>
              <span class="value">${tile.value}</span>
              <span class="label">${tile.label}</span>
            </a>
          `,
        )}
      </div>
    `;
  }

  /** Operators: the clawlets that wait for an approval, which only the Servers page gives. */
  renderRequests({ requests }) {
    if (requests.length === 0) {
      return null;
    }
    // the id a clawlet asks for is its own word, and it may ask for none
    const servers = requests.map((request) => request.requestedServerId).filter(Boolean);
    return html`
      <div class="notice" data-tone="warning" role="status">
        <strong>${requests.length === 1 ? 'A clawlet waits' : `${requests.length} clawlets wait`} for approval</strong>
        ${servers.length ? html`Asking for ${servers.map((id, i) => html`${i ? ', ' : ''}<code>${id}</code>`)}: ` : null}<a href="/servers">approve or deny in Servers</a>.
      </div>
    `;
  }

  renderAttention({ instances }) {
    const attention = instances.filter((instance) => ATTENTION_STATES.includes(instance.state)).sort((a, b) => urgency(a.state) - urgency(b.state));
    if (attention.length === 0) {
      return instances.length === 0
        ? html`<p class="empty">No workspace yet: the operator assigns them.</p>`
        : html`<p class="all-good">Every workspace runs the revision it should.</p>`;
    }
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Workspace</th>
              <th>State</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            ${attention.map(
              (instance) => html`
                <tr>
                  <td>
                    <a href="/workspaces/${instance.ref ?? instance.name}">${instance.displayName}</a> <code class="muted">${instance.name}</code>
                    <div class="muted">${instance.customer}</div>
                  </td>
                  <td>${stateBadge(instance.state)}</td>
                  <td class="why wrap">${instance.error ?? stateHint(instance)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  renderServers({ servers }) {
    return html`
      <div class="table-wrap">
        <table>
          <tbody>
            ${servers.map(
              (server) => html`
                <tr>
                  <td><a href="/servers/${server.id}"><code>${server.id}</code></a> <span class="muted">${server.name}</span></td>
                  <td>${connectionBadge(server.connection)}</td>
                  <td class="muted">${server.connection === 'online' ? when(server.connectedSince) : when(server.disconnectedAt, 'never')}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    const head = html`
      <header class="page-head">
        <div>
          <h1>Overview</h1>
          <p class="lead">
            ${this.poll.value && !this.poll.value.viewer.operator
              ? `The workspaces of ${this.poll.value.viewer.customers.join(', ') || 'your account'}, as their servers report them.`
              : 'Managed workspaces and the servers they run on, as the servers report them.'}
          </p>
        </div>
      </header>
    `;
    const pending = loadState(this.poll, 'the overview');
    if (pending) {
      return html`${head}${pending}`;
    }
    const data = this.poll.value;
    return html`
      ${head} ${staleWarning(this.poll)} ${this.renderTiles(data)}
      <div class="columns">
        <div>
          <section>
            <h2>Needs attention</h2>
            ${this.renderRequests(data)} ${this.renderAttention(data)}
          </section>
          ${data.servers
            ? html`<section>
                <h2>Servers</h2>
                ${this.renderServers(data)}
              </section>`
            : null}
        </div>
        <section>
          <h2>Latest applies</h2>
          <plane-event-list .events=${data.events} show-instance limit="6"></plane-event-list>
        </section>
      </div>
    `;
  }
}

customElements.define('plane-overview-page', OverviewPage);
